'use client';

import { useTranslation } from '@/features/i18n';
import { VideoProgressBar } from '@/features/resume-watching';
import { PAYMENTS_ENABLED, TVOD_ENABLED } from '@/util/constants';
import { useAccessToAsset } from '@vimond/features/authentication';
import { useVideoProgressPosition } from '@vimond/features/resume-watching';
import { useLocale } from '@vimond/i18n';
import { AssetType } from '@vimond/services/content-discovery';
import { cn } from '@vimond/theme/tailwind';
import { Link } from '../Link';
import { Button } from './Button';

// For now, these are exclusive
type PaymentMode = 'SVOD' | 'TVOD';

const PAYMENTS_MODE: PaymentMode = TVOD_ENABLED ? 'TVOD' : 'SVOD';

type Props = {
  asset?: AssetType;
};

export const WatchVideoButton = ({ asset }: Props) => {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { progress } = useVideoProgressPosition(asset?.id, locale);
  const { hasAccess } = useAccessToAsset(asset);

  if (!asset) return <></>;

  const href = `/watch/${asset.id}`;

  const buttonText = () => {
    if (PAYMENTS_ENABLED && !hasAccess) {
      if (PAYMENTS_MODE === 'TVOD') return t('subscriptions.rent');
      return t('subscriptions.subscribe');
    }
    if (progress) return t('resume-watching');
    if (asset.isLive) return t('watch-live');
    if (!(asset.season ?? asset.episode)) return t('watch-now');
    if (asset.season === 1 && asset.episode === 1)
      return t('watch-first-episode');

    const text = [t('watch')];
    if (asset.season) text.push(`S${asset.season}`);
    if (asset.episode) text.push(`E${asset.episode}`);
    return text.join(' ');
  };

  return (
    <Link href={href}>
      <div
        className={cn(
          'relative flex flex-col gap-3 lg:w-[250px]',
          progress > 0 && 'mb-3',
        )}
      >
        <Button
          variant="primary"
          icon={hasAccess || !PAYMENTS_ENABLED ? 'play' : 'lock'}
          className="lg:w-[250px]"
        >
          {buttonText()}
        </Button>
        {progress > 0 && (
          <div className="absolute -bottom-3 left-0 right-0">
            <VideoProgressBar asset={asset} />
          </div>
        )}
      </div>
    </Link>
  );
};
